// apps/web/src/components/Header.tsx

import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useTheme } from "next-themes";
import { User, Search, LogOut, Sun, Moon, Monitor, Check, Settings as SettingsIcon, LayoutDashboard, Shield, Menu, FileText } from "lucide-react";
import { Notifications } from "./Notifications";
import { useState, useEffect, useRef } from "react";
import { trpc } from "@/trpc/client";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { getAvatarUrl } from "@/lib/utils";
import { motion } from "framer-motion";

// Magnetic Button Component for ultra-premium feel
const MagneticButton = ({ children, className, onClick }: any) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouse = (e: React.MouseEvent) => {
    const { clientX, clientY } = e;
    const { height, width, left, top } = ref.current!.getBoundingClientRect();
    const middleX = clientX - (left + width / 2);
    const middleY = clientY - (top + height / 2);
    setPosition({ x: middleX, y: middleY });
  };

  const reset = () => {
    setPosition({ x: 0, y: 0 });
  };

  const { x, y } = position;
  return (
    <motion.button
      ref={ref}
      className={className}
      animate={{ x, y }}
      transition={{ type: "spring", stiffness: 150, damping: 15, mass: 0.1 }}
      onMouseMove={handleMouse}
      onMouseLeave={reset}
      onClick={onClick}
    >
      {children}
    </motion.button>
  );
}

export const Header = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { setTheme, theme, resolvedTheme } = useTheme();

  // Search functionality
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Search jobs
  const { data: searchResults } = trpc.job.getAll.useQuery(
    undefined,
    { enabled: open && searchQuery.length > 0 }
  );

  // Filter results based on search query
  const filteredJobs = searchResults?.filter(job =>
    job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.pickupDistrict.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.deliveryDistrict.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Keyboard shortcut forsearch (Cmd+K or Ctrl+K)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "circOut" }}
        className="fixed top-0 left-0 right-0 z-50 border-b border-foreground/5 backdrop-blur-xl bg-background/80 supports-[backdrop-filter]:bg-background/80"
      >
        <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-50" />

        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo with magnetic hover */}
            <Link to="/" className="flex items-center space-x-3 group relative z-50">
              <motion.div
                whileHover={{ rotate: 180, scale: 1.1 }}
                transition={{ duration: 0.5, ease: "circOut" }}
              >
                <img
                  src={resolvedTheme === 'dark'
                    ? "https://res.cloudinary.com/unihousingmw/image/upload/v1765271781/favicon-32x32_vrwdzo.png"
                    : "https://res.cloudinary.com/unihousingmw/image/upload/v1765271781/transparent-favicon_ezmbmf.png"
                  }
                  alt="K&C Logistics"
                  className="w-10 h-10 object-contain drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                />
              </motion.div>
            </Link>

            {/* Search Bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-md mx-8">
              <div
                className="w-full relative group cursor-pointer"
                onClick={() => setOpen(true)}
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
                <div className="relative flex items-center w-full px-4 py-2 bg-foreground/5 border border-foreground/10 rounded-lg text-sm text-muted-foreground backdrop-blur-sm group-hover:border-primary/30 transition-colors">
                  <Search className="mr-3 w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground/70">Search global network...</span>
                  <kbd className="pointer-events-none ml-auto inline-flex h-5 select-none items-center gap-1 rounded border border-foreground/10 bg-foreground/5 px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                    <span className="text-xs">⌘</span>K
                  </kbd>
                </div>
              </div>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2">
              {/* Mobile Search Button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden text-muted-foreground hover:text-foreground"
                onClick={() => setOpen(true)}
              >
                <Search className="h-5 w-5" />
              </Button>

              {/* Theme Toggle - Light / Dark / System */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hover:bg-foreground/5 rounded-full text-foreground relative"
                  >
                    <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                    <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                    <span className="sr-only">Toggle theme</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40 bg-background/95 backdrop-blur-xl border-foreground/10">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">Theme</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => setTheme("light")}
                    className="flex items-center gap-2 cursor-pointer focus:bg-foreground/5 focus:text-foreground"
                  >
                    <Sun className="h-4 w-4" />
                    Light
                    {theme === "light" && <Check className="h-4 w-4 ml-auto text-primary" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setTheme("dark")}
                    className="flex items-center gap-2 cursor-pointer focus:bg-foreground/5 focus:text-foreground"
                  >
                    <Moon className="h-4 w-4" />
                    Dark
                    {theme === "dark" && <Check className="h-4 w-4 ml-auto text-primary" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setTheme("system")}
                    className="flex items-center gap-2 cursor-pointer focus:bg-foreground/5 focus:text-foreground"
                  >
                    <Monitor className="h-4 w-4" />
                    System
                    {theme === "system" && <Check className="h-4 w-4 ml-auto text-primary" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {user ? (
                <>
                  <Notifications />

                  {/* Post Job Button - Client Only */}
                  {user.role === 'CLIENT' && (
                    <Button
                      asChild
                      variant="default"
                      size="sm"
                      className="hidden sm:flex bg-foreground text-background hover:bg-foreground/90 font-semibold rounded-full px-6 shadow-[0_0_20px_rgba(0,0,0,0.1)] hover:shadow-[0_0_30px_rgba(0,0,0,0.2)] dark:shadow-[0_0_20px_rgba(255,255,255,0.3)] dark:hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] transition-all duration-300"
                    >
                      <Link to="/jobs/create">Post Shipment</Link>
                    </Button>
                  )}

                  {/* User Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        id="user-menu-trigger"
                        variant="ghost"
                        size="icon"
                        className="rounded-full relative w-10 h-10 border border-foreground/10 hover:border-primary/50 transition-colors overflow-hidden"
                      >
                        {user.profile?.profilePicturePath ? (
                          <img
                            src={getAvatarUrl(user.profile.profilePicturePath) || ''}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-tr from-primary to-purple-600 flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-background/90 backdrop-blur-xl border-foreground/10 text-foreground p-2">
                      <DropdownMenuLabel>
                        <p className="font-semibold">{user.profile?.fullName || 'User'}</p>
                        <p className="text-xs text-muted-foreground font-normal">{user.email}</p>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-foreground/10" />

                      {user.role === 'ADMIN' ? (
                        <>
                          <DropdownMenuItem className="focus:bg-foreground/5 focus:text-foreground cursor-pointer" onClick={() => navigate('/admin/dashboard')}>
                            <Shield className="mr-2 h-4 w-4" />
                            Admin Dashboard
                          </DropdownMenuItem>
                          <DropdownMenuItem className="focus:bg-foreground/5 focus:text-foreground cursor-pointer" onClick={() => navigate('/admin/users')}>
                            <User className="mr-2 h-4 w-4" />
                            Manage Users
                          </DropdownMenuItem>
                          <DropdownMenuItem className="focus:bg-foreground/5 focus:text-foreground cursor-pointer" onClick={() => navigate('/admin/verifications')}>
                            <Shield className="mr-2 h-4 w-4" />
                            Verifications
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <>
                          <DropdownMenuItem className="focus:bg-foreground/5 focus:text-foreground cursor-pointer" onClick={() => navigate('/dashboard')}>
                            <LayoutDashboard className="mr-2 h-4 w-4" />
                            Dashboard
                          </DropdownMenuItem>
                          <DropdownMenuItem className="focus:bg-foreground/5 focus:text-foreground cursor-pointer" onClick={() => navigate('/dashboard/profile')}>
                            <User className="mr-2 h-4 w-4" />
                            Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem className="focus:bg-foreground/5 focus:text-foreground cursor-pointer" onClick={() => navigate('/dashboard/receipts')}>
                            <FileText className="mr-2 h-4 w-4" />
                            Cash Slips
                          </DropdownMenuItem>
                        </>
                      )}

                      <DropdownMenuItem className="focus:bg-foreground/5 focus:text-foreground cursor-pointer" onClick={() => navigate('/dashboard/settings')}>
                        <SettingsIcon className="mr-2 h-4 w-4" />
                        Settings
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-foreground/10" />
                      <DropdownMenuItem
                        onClick={() => logout()}
                        className="text-red-500 focus:text-red-600 focus:bg-red-500/10 cursor-pointer"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-4">
                    <Link to="/blog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                      Blog
                    </Link>
                    <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                      Log In
                    </Link>
                    <Button asChild className="rounded-full bg-foreground text-background hover:bg-foreground/90 font-bold px-6">
                      <Link to="/register">Sign Up</Link>
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.header>

      {/* Search Dialog */}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search global shipments..."
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList>
          <CommandEmpty>
            No active shipments found.
          </CommandEmpty>
          {filteredJobs && filteredJobs.length > 0 && (
            <CommandGroup heading="Active Jobs">
              {filteredJobs.slice(0, 10).map((job) => (
                <CommandItem
                  key={job.id}
                  onSelect={() => {
                    navigate(`/jobs/${job.id}`);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <div className="flex flex-col gap-1 w-full">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{job.title}</span>
                      <span className="text-xs text-muted-foreground">
                        MWK {job.priceAmount.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{job.pickupDistrict}</span>
                      <span>→</span>
                      <span>{job.deliveryDistrict}</span>
                      {job.loadCount && job.loadCount > 1 && (
                        <>
                          <span>•</span>
                          <span>{job.loadCount} loads</span>
                        </>
                      )}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};